import { UAParser } from 'ua-parser-js';
import { ClientMetadata, NizzaTimeZone } from '../types';
import { getClientTimeZone } from './getClientTimeZone';

/**
 * Composes and returns extended client metadata.
 *
 * This function aggregates metadata from user agent, language, screen, connection,
 * hardware, and timezone into a single object that conforms to the ClientMetadata API.
 *
 * @returns {ClientMetadata} The client metadata object.
 */
export function getClientMetadata(): ClientMetadata {
  const isBrowser = typeof navigator !== 'undefined';
  const userAgent = isBrowser ? navigator.userAgent : 'server';

  // Retrieve metadata using specialized functions
  const uaMetadata = getUserAgentMetadata(userAgent);
  const { language, languages } = getLanguageMetadata();
  const screen = getScreenMetadata();
  const connection = getConnectionMetadata();
  const { hardwareConcurrency, deviceMemory } = getHardwareMetadata();
  const timezone = getTimezoneMetadata();

  return {
    userAgent,
    browser: uaMetadata.browser,
    os: uaMetadata.os,
    device: uaMetadata.device,
    language,
    languages,
    screen,
    connection,
    hardwareConcurrency,
    deviceMemory,
    timezone,
  };
}

/**
 * Retrieves user agent metadata using UAParser.js.
 *
 * @param {string} userAgent - The user agent string.
 * @returns {{
 *   browser: { name: string, version: string },
 *   os: { name: string, version: string },
 *   device: { model: string, type: string, vendor: string }
 * }} An object containing browser, OS, and device information.
 */
function getUserAgentMetadata(userAgent: string): {
  browser: { name: string; version: string };
  os: { name: string; version: string };
  device: { model: string; type: string; vendor: string };
} {
  try {
    const parser = new UAParser(userAgent);
    const result = parser.getResult();

    return {
      browser: {
        name: result.browser.name || 'Unknown',
        version: result.browser.version || 'Unknown',
      },
      os: {
        name: result.os.name || 'Unknown',
        version: result.os.version || 'Unknown',
      },
      device: {
        model: result.device.model || 'Unknown',
        // For desktop, UAParser.js may not set device.type; fallback to 'Desktop'
        type: result.device.type || 'Desktop',
        vendor: result.device.vendor || 'Unknown',
      },
    };
  } catch (error) {
    console.error('Error parsing user agent:', error);
    return {
      browser: { name: 'Unknown', version: 'Unknown' },
      os: { name: 'Unknown', version: 'Unknown' },
      device: { model: 'Unknown', type: 'Desktop', vendor: 'Unknown' },
    };
  }
}

/**
 * Retrieves language metadata from the navigator.
 *
 * @returns {{ language: string, languages: string[] }} An object containing the primary language and array of languages.
 */
function getLanguageMetadata(): { language: string; languages: string[] } {
  if (typeof navigator !== 'undefined') {
    return {
      language: navigator.language || 'Unknown',
      languages: navigator.languages ? [...navigator.languages] : [],
    };
  }
  return { language: 'Unknown', languages: [] };
}

/**
 * Retrieves screen metadata from the window.
 *
 * @returns {{ width: number, height: number, colorDepth: number } | null}
 *          An object with screen width, height, and color depth or null if unavailable.
 */
function getScreenMetadata(): {
  width: number;
  height: number;
  colorDepth: number;
} | null {
  if (typeof window !== 'undefined' && window.screen) {
    return {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
    };
  }
  return null;
}

/**
 * Retrieves connection metadata from the navigator.
 *
 * @returns {{
 *   downlink: number | null,
 *   effectiveType: string,
 *   rtt: number | null,
 *   saveData: boolean
 * } | null} An object with connection details or null if unavailable.
 */
function getConnectionMetadata(): {
  downlink: number;
  effectiveType: string;
  rtt: number;
  saveData: boolean;
} | null {
  if (typeof navigator !== 'undefined' && 'connection' in navigator) {
    const connection = (navigator as any).connection;
    return {
      downlink: connection?.downlink ?? 0,
      effectiveType: connection?.effectiveType ?? 'Unknown',
      rtt: connection?.rtt ?? 0,
      saveData: connection?.saveData ?? false,
    };
  }
  return null;
}

/**
 * Retrieves hardware metadata from the navigator.
 *
 * @returns {{ hardwareConcurrency: number | null, deviceMemory: number | null }}
 *          An object with hardware concurrency and device memory information.
 */
function getHardwareMetadata(): {
  hardwareConcurrency: number | null;
  deviceMemory: number | null;
} {
  if (typeof navigator !== 'undefined') {
    return {
      hardwareConcurrency: navigator.hardwareConcurrency || null,
      deviceMemory: (navigator as any).deviceMemory || null,
    };
  }
  return { hardwareConcurrency: null, deviceMemory: null };
}

/**
 * Retrieves the client's timezone metadata.
 *
 * This function uses getClientTimeZone to obtain the client's time zone information.
 * It ensures that any error does not block the program flow.
 *
 * @returns {NizzaTimeZone} The client's timezone information or a fallback object if an error occurs.
 */
function getTimezoneMetadata(): NizzaTimeZone {
  try {
    return getClientTimeZone(true);
  } catch (error) {
    console.error('Error retrieving client timezone:', error);
    return {
      offset: null,
      offsetValue: null,
      value: 'Unknown',
      country: null,
      included: null,
      label: null,
    };
  }
}
